/**
 * Stage 2: chain cleaned wall segments into one closed perimeter loop.
 *
 * Wall endpoints within joinTol merge into graph nodes. Dead-end wall pairs
 * (RoomPlan sometimes leaves a passage completely unwalled) are bridged with
 * synthetic walls, preferring an axis-aligned L whose corner lands on
 * existing wall lines, so the bridged perimeter stays rectilinear.
 *
 * The loop is the simple cycle using the FEWEST synthetic bridges, then the
 * greatest total wall length. Fewest-bridges-first keeps a bogus bridge from
 * ever beating a real wall path; greatest-length resolves RoomPlan's
 * duplicate-wall conflicts (it emits both an alcove's walls AND a synthesized
 * wall bridging the alcove's mouth; the longer detour wins).
 */

import { ringSignedArea } from '../geometry';
import { Point } from '../schemas';
import { ConversionOptions, ConversionReport } from './conversionTypes';
import { CleanWall, dist, simplifyRing } from './rawGeometry';

interface GraphWall extends CleanWall {
  na: number;
  nb: number;
}

/** Merge endpoints within joinTol into shared node ids; returns node positions. */
function buildGraph(walls: CleanWall[], joinTol: number): { nodes: Point[]; graphWalls: GraphWall[] } {
  const nodes: Point[] = [];
  const nodeFor = (p: Point): number => {
    for (let i = 0; i < nodes.length; i++) if (dist(nodes[i], p) <= joinTol) return i;
    nodes.push({ ...p });
    return nodes.length - 1;
  };
  const graphWalls = walls
    .map(w => ({ ...w, na: nodeFor(w.a), nb: nodeFor(w.b) }))
    .filter(w => w.na !== w.nb);
  return { nodes, graphWalls };
}

/** A synthetic bridge wall, flattened onto the axis when it's near-axis: an
 *  un-snapped 0.1 ft slope over a long bridge would read as a diagonal wall
 *  downstream (breaking chamfer detection on bay protrusions). */
const mkWall = (id: string, a: Point, b: Point, axisTol: number): CleanWall => {
  if (Math.abs(b.y - a.y) <= axisTol) {
    const y = (a.y + b.y) / 2;
    a = { x: a.x, y };
    b = { x: b.x, y };
    return { id, a, b, synthetic: true, length: dist(a, b), axis: 'h' };
  }
  if (Math.abs(b.x - a.x) <= axisTol) {
    const x = (a.x + b.x) / 2;
    a = { x, y: a.y };
    b = { x, y: b.y };
    return { id, a, b, synthetic: true, length: dist(a, b), axis: 'v' };
  }
  return { id, a, b, synthetic: true, length: dist(a, b), axis: 'd' };
};

/** The wall ends no other wall meets (degree-1 graph nodes). */
function deadEnds(walls: CleanWall[], joinTol: number): Point[] {
  const { nodes, graphWalls } = buildGraph(walls, joinTol);
  const degree = new Map<number, number>();
  for (const w of graphWalls) {
    degree.set(w.na, (degree.get(w.na) ?? 0) + 1);
    degree.set(w.nb, (degree.get(w.nb) ?? 0) + 1);
  }
  return [...degree.entries()].filter(([, d]) => d === 1).map(([n]) => nodes[n]);
}

/** Greedy nearest-pair matching: the closest two points pair off, then the closest two left, and so on. */
function nearestPairs(points: Point[]): [Point, Point][] {
  const pairs: [Point, Point][] = [];
  const left = [...points];
  while (left.length >= 2) {
    let bi = 0, bj = 1, bd = Infinity;
    for (let i = 0; i < left.length; i++) {
      for (let j = i + 1; j < left.length; j++) {
        if (dist(left[i], left[j]) < bd) {
          bd = dist(left[i], left[j]);
          bi = i;
          bj = j;
        }
      }
    }
    pairs.push([left[bi], left[bj]]);
    left.splice(bj, 1);
    left.splice(bi, 1);
  }
  return pairs;
}

/**
 * The synthetic walls bridging dead ends `a` and `b`: one straight wall when
 * they line up, otherwise an axis-aligned L. Of the L's two corner choices,
 * the one whose legs lie on more existing wall lines wins.
 */
function bridgeWalls(a: Point, b: Point, index: number, walls: CleanWall[], lineTol: number): CleanWall[] {
  if (Math.abs(a.x - b.x) <= lineTol || Math.abs(a.y - b.y) <= lineTol) return [mkWall(`gap:${index}`, a, b, lineTol)];
  const onVLine = (x: number) => walls.some(w => w.axis === 'v' && Math.abs(w.a.x - x) < lineTol);
  const onHLine = (y: number) => walls.some(w => w.axis === 'h' && Math.abs(w.a.y - y) < lineTol);
  const support1 = (onVLine(a.x) ? 1 : 0) + (onHLine(b.y) ? 1 : 0);
  const support2 = (onVLine(b.x) ? 1 : 0) + (onHLine(a.y) ? 1 : 0);
  const corner = support1 >= support2 ? { x: a.x, y: b.y } : { x: b.x, y: a.y };
  return [mkWall(`gap:${index}a`, a, corner, lineTol), mkWall(`gap:${index}b`, corner, b, lineTol)];
}

/** The report line for a bridged gap: a loud one past `maxGapBridge`. */
function bridgeWarning(a: Point, b: Point, maxGapBridge: number): string {
  const gap = dist(a, b);
  return gap > maxGapBridge
    ? `Bridged a LARGE ${gap.toFixed(1)} ft perimeter gap near (${a.x.toFixed(1)}, ${
      a.y.toFixed(1)
    }) is well beyond the ${maxGapBridge} ft confidence range; review this wall`
    : `Bridged a ${gap.toFixed(1)} ft perimeter gap near (${a.x.toFixed(1)}, ${a.y.toFixed(1)}) with synthetic wall(s); there is no scan data there`;
}

/**
 * Synthesize walls between dead-end (degree-1) node pairs, nearest pairs
 * first. Every pair is bridged, since dead ends guarantee no loop otherwise;
 * each bridge is reported, and a gap beyond maxGapBridge loudly. Off-axis
 * gaps become an axis-aligned L whose corner follows the scan's wall lines,
 * the best evidence for where the unscanned boundary actually runs.
 */
function bridgeDeadEnds(
  walls: CleanWall[],
  opts: ConversionOptions,
  report: ConversionReport,
): CleanWall[] {
  const ends = deadEnds(walls, opts.joinTol);
  if (ends.length < 2) return walls;
  const bridges = nearestPairs(ends).flatMap(([a, b], index) => {
    report.warnings.push(bridgeWarning(a, b, opts.maxGapBridge));
    return bridgeWalls(a, b, index, walls, opts.lineClusterTol);
  });
  return [...walls, ...bridges];
}

/** DFS for the simple cycle with fewest synthetic bridges, then max total length. */
function bestCycle(graphWalls: GraphWall[], nodeCount: number): GraphWall[] {
  const byNode: GraphWall[][] = Array.from({ length: nodeCount }, () => []);
  for (const w of graphWalls) {
    byNode[w.na].push(w);
    byNode[w.nb].push(w);
  }

  let best: GraphWall[] = [];
  let bestLen = 0, bestBridges = Infinity;
  const realWalls = graphWalls.filter(w => !w.synthetic);
  const start = (realWalls.length ? realWalls : graphWalls)
    .reduce((m, w) => (w.length > m.length ? w : m));
  const used = new Set<GraphWall>([start]);
  const visited = new Set<number>([start.na]);
  const path: GraphWall[] = [start];

  const walk = (at: number, len: number, bridges: number): void => {
    for (const w of byNode[at]) {
      if (used.has(w)) continue;
      const next = w.na === at ? w.nb : w.na;
      const nb = bridges + (w.synthetic ? 1 : 0);
      if (next === start.na) {
        if (path.length >= 2 && (nb < bestBridges || (nb === bestBridges && len + w.length > bestLen))) {
          bestBridges = nb;
          bestLen = len + w.length;
          best = [...path, w];
        }
        continue;
      }
      if (visited.has(next)) continue;
      used.add(w);
      visited.add(next);
      path.push(w);
      walk(next, len + w.length, nb);
      used.delete(w);
      visited.delete(next);
      path.pop();
    }
  };
  walk(start.nb, start.length, start.synthetic ? 1 : 0);
  return best;
}

/** Intersection of two walls' support lines; falls back to the joint node position. */
function cornerPoint(w1: GraphWall, w2: GraphWall, joint: Point): Point {
  const d1 = { x: w1.b.x - w1.a.x, y: w1.b.y - w1.a.y };
  const d2 = { x: w2.b.x - w2.a.x, y: w2.b.y - w2.a.y };
  const cross = d1.x * d2.y - d1.y * d2.x;
  if (Math.abs(cross) < 1e-9 * Math.hypot(d1.x, d1.y) * Math.hypot(d2.x, d2.y)) return joint;
  const t = ((w2.a.x - w1.a.x) * d2.y - (w2.a.y - w1.a.y) * d2.x) / cross;
  return { x: w1.a.x + d1.x * t, y: w1.a.y + d1.y * t };
}

/**
 * The cycle's vertices: where each wall's support line meets the next one's.
 * The cycle is node-linked, so the node two consecutive walls share anchors
 * the parallel-lines fallback.
 */
const cycleVertices = (cycle: GraphWall[], nodes: Point[]): Point[] =>
  cycle.map((w, i) => {
    const next = cycle[(i + 1) % cycle.length];
    const jointNode = w.na === next.na || w.na === next.nb ? w.na : w.nb;
    return cornerPoint(w, next, nodes[jointNode]);
  });

/**
 * The perimeter as a CCW simple polygon (in the feet, y-up working frame).
 * Vertices are the intersections of consecutive walls' support lines, so
 * axis-snapped corners come out exactly square. Also returns the wall list
 * with any synthetic bridge walls appended (primaryRect needs it).
 */
export function buildPerimeterLoop(
  walls: CleanWall[],
  opts: ConversionOptions,
  report: ConversionReport,
): { ring: Point[]; walls: CleanWall[] } {
  const bridged = bridgeDeadEnds(walls, opts, report);
  const { nodes, graphWalls } = buildGraph(bridged, opts.joinTol);
  if (graphWalls.length < 3) throw new Error('Not enough walls to form a perimeter');
  const cycle = bestCycle(graphWalls, nodes.length);
  if (cycle.length < 3) throw new Error('No closed perimeter loop found in wall graph');

  const usedIds = new Set(cycle.map(w => w.id));
  report.droppedWallIds.push(...walls.filter(w => !usedIds.has(w.id)).map(w => w.id));

  const clean = simplifyRing(cycleVertices(cycle, nodes), 1e-3);
  return { ring: ringSignedArea(clean) < 0 ? clean.reverse() : clean, walls: bridged };
}
